import type { SalesforcePaymentsCheckoutConfig } from '../../types/config';
import type { BillingDetails, PaymentIntentRequest, ShippingDetails } from '../../types/paymentRequest';
import type { PaymentResponse } from '../../types/paymentResponse';
import type { PaymentMethodSet, VendorAccount } from '../../types/paymentMethodSet';
import { ComponentType, PaymentMethodType } from '../../types/paymentMethodSet';
import { addClass } from '../../util/utility';
import type { SfPaymentMethodComponent } from '../../vendor/sfp-payment-method-component';
import type { SfpVendor } from '../../vendor/sfp-vendor';
import { PaymentSheet } from '../paymentSheet';
import type { SfpSavedPaymentMethodComponent } from '../../vendor/sfp-saved-payment-method-component';
import type SfpContext from '../../sfp-context';
import type { MetadataReader } from '../../util/metadata-reader';
import type { VendorPaymentMethodElement, VendorPaymentMethodElementMapping } from '../../types/component';
import type { CheckoutTemplate } from '../template/checkout-template';
import { checkAmount } from '../../util/preconditions';

export class Checkout extends PaymentSheet {
  TYPE = ComponentType.CHECKOUT;

  paymentRequest: PaymentIntentRequest;
  config: SalesforcePaymentsCheckoutConfig;
  template: CheckoutTemplate;

  /**
   * @class Checkout
   * @hideconstructor
   * @classdesc Checkout component presenting a selection of payment methods determined by the payment method set and related configuration.
   * @extends Component
   * @public
   */
  constructor(
    context: SfpContext,
    metadataReader: MetadataReader,
    paymentMethodSet: PaymentMethodSet,
    config: SalesforcePaymentsCheckoutConfig,
    paymentRequest: PaymentIntentRequest,
    elementOrSelectorToMountTo: string | HTMLElement
  ) {
    super(context, metadataReader, paymentMethodSet, config, paymentRequest, elementOrSelectorToMountTo);
  }

  async updateAmount(amount: number): Promise<void> {
    checkAmount(amount, 'amount');

    // Update each of the components
    await Promise.allSettled(this.components.map((component) => component.updateAmount(amount)));

    // Set the updated amount in the payment request
    this.paymentRequest.amount = amount;
  }

  protected createPaymentElement(): void {
    super.createPaymentElement();

    /**
     * Shows the Apple Pay payment app, if the currently selected payment method in the payment sheet is Apple Pay.
     * Bubble a <code>showapplepay</code> event to a Checkout component if you want to show the payment app
     * before calling the {@link Checkout#confirm} function. If you don't call <code>confirm</code> within 10 seconds
     * the payment app will be automatically aborted.
     * @name Checkout#Event listener - showapplepay
     * @public
     */
    this.paymentElement.addEventListener('showapplepay', () =>
      this.selectedPaymentMethod.fireEvent({
        type: 'showApplePay',
      })
    );
    /**
     * Aborts the Apple Pay payment app and hides its popup, if the currently selected payment method in the payment sheet
     * is Apple Pay, and the payment app is showing or in the process of being shown. Bubble an <code>abortapplepay</code> event
     * to a Checkout component to cancel a previously bubbled <code>showapplepay</code> event.
     * @name Checkout#Event listener - abortapplepay
     * @public
     */
    this.paymentElement.addEventListener('abortapplepay', () =>
      this.selectedPaymentMethod.fireEvent({
        type: 'abortApplePay',
      })
    );
  }

  protected stylePaymentElement(): void {
    super.stylePaymentElement();

    addClass(this.paymentElement, 'sfpp-component-checkout');
  }

  protected async getComponents(): Promise<void> {
    await this.template.buildSavedPaymentMethodsElements();
    await super.getComponents();
  }

  public async getSavedPaymentMethodsComponents(
    createChoiceElement: (paymentComponent: SfpSavedPaymentMethodComponent, selectDefault: boolean) => void
  ): Promise<void> {
    const savedPaymentMethods = this.config.options.savedPaymentMethods ?? [];

    for (const savedPaymentMethod of savedPaymentMethods) {
      let vendor;
      try {
        const paymentMethodType: PaymentMethodType =
          PaymentMethodType[(savedPaymentMethod.type || '').toUpperCase() as keyof typeof PaymentMethodType];
        if (paymentMethodType) {
          if (this.metadataReader.currencyIsApplicable(paymentMethodType, this.paymentRequest.currency)) {
            // Get the vendor account for the SPM
            const vendorAccount = this.getAccountForSavedPaymentMethod(savedPaymentMethod.gatewayId);
            if (vendorAccount) {
              vendor = vendorAccount.vendor;
              // Map payment method element to render for this payment method
              const mapping: VendorPaymentMethodElementMapping = this.template.mapVendorPaymentMethodType(
                vendor,
                paymentMethodType,
                true
              );
              if (mapping) {
                const vendorPaymentMethodElement: VendorPaymentMethodElement = {
                  vendorAccount,
                  paymentMethodElement: mapping.paymentMethodElement,
                  paymentMethods: [
                    {
                      vendorAccount,
                      paymentMethodType,
                    },
                  ],
                };
                // Obtain handler for the vendor
                const vendorHandler: SfpVendor = await this.obtainVendorHandler(vendorAccount);

                // Get component for the SPM
                const paymentComponent = await vendorHandler.getSavedPaymentMethodComponent(
                  this,
                  vendorPaymentMethodElement,
                  savedPaymentMethod,
                  this.metadataReader,
                  this.config,
                  this.paymentRequest
                );
                if (paymentComponent) {
                  paymentComponent.vendorAccount = vendorAccount;
                  const selectDefault =
                    paymentComponent.savedPaymentMethod.isDefault ||
                    savedPaymentMethods.indexOf(savedPaymentMethod) === 0;
                  createChoiceElement(paymentComponent, selectDefault);

                  if (selectDefault) {
                    this.selectedPaymentMethod = paymentComponent;
                  }
                }
              } else {
                console.warn(`Element not found for ${vendor} to render ${paymentMethodType}`);
                this.context.report({
                  code: 'VENDOR_PAYMENT_METHOD_NOT_FOUND',
                  vendor: vendor,
                  type: 'PAYMENT_METHOD_SET',
                  message: `Element not found for ${vendor} to render ${paymentMethodType}`,
                });
              }
            }
          }
        } else {
          console.warn(`Saved payment method type ${savedPaymentMethod.type} not supported`);
          this.context.report({
            code: 'VENDOR_PAYMENT_METHOD_NOT_FOUND',
            vendor: vendor,
            message: `Saved payment method type ${savedPaymentMethod.type} not supported`,
          });
        }
      } catch (err) {
        console.warn(`SPM component creation for ${savedPaymentMethod.type} failed: ${err}`);
        this.context.report({
          code: 'COMPONENT_CREATION_FAILED',
          vendor: vendor,
          message: `SPM component creation for ${savedPaymentMethod.type} failed: ${err}`,
        });
      }
    }
  }

  protected shouldAddComponent(paymentComponent: SfPaymentMethodComponent): boolean {
    const canSavePayment = this.metadataReader.canSavePayment(paymentComponent.getPaymentMethodType());
    if (this.config.options.enforceSavedPaymentMethod && !canSavePayment) {
      //if payment method saving is enforced then the provided payment component should be savable.
      return false;
    }
    return super.shouldAddComponent(paymentComponent);
  }

  private getAccountForSavedPaymentMethod(gatewayId: string): VendorAccount {
    return this.paymentMethodSet.paymentMethodSetAccounts.find((account) => account.gatewayId.startsWith(gatewayId));
  }

  /**
   * Confirms payment for the currently selected payment method using the values input by the user.
   * @param {createIntent} [createIntent] - Asynchronous function to create a Stripe PaymentIntent to be confirmed. If not provided here, the [SalesforcePaymentsCheckoutActions.createIntent]{@link SalesforcePaymentsCheckoutActions} is used instead.
   * @param {BillingDetails} [billingDetails] - Billing information to associate with the payment. If not provided here, the [PaymentIntentRequest.billingDetails]{@link PaymentIntentRequest} is used instead.
   * @param {ShippingDetails} [shippingDetails] - Shipping information to associate with the payment.
   * @returns {Promise<PaymentResponse>} - Promise that resolves with a success/fail response to payment confirmation, or rejects if there was an error attempting confirmation.
   * @public
   */
  async confirm(
    createIntent?: Function,
    billingDetails?: BillingDetails,
    shippingDetails?: ShippingDetails
  ): Promise<PaymentResponse> {
    this.paymentRequest.billingDetails = billingDetails;

    return await this.selectedPaymentMethod.handleConfirm(
      createIntent ? createIntent : this.config.actions.createIntent,
      billingDetails,
      shippingDetails
    );
  }
}
